import {
  accountBalance,
  buildAddAccount,
  buildAddExpected,
  buildAdjustBalance,
  buildDeleteAccount,
  buildDeleteExpected,
  buildEditAccount,
  buildEditExpected,
  buildReceiveExpected,
  buildReceiveOccurrence,
  buildSkipOccurrence,
  buildUndoOccurrence,
  isRepeating,
  buildRestoreAccount,
  buildRestoreExpected,
  buildRestoreIncomeSource,
  buildRestoreTransfer,
  buildTransfer,
  buildUndoReceiveExpected,
  buildUndoTransfer,
  receiveIntoAccount,
  buildAddReceivable,
  buildDeleteReceivable,
  buildEditReceivable,
  buildReceiveReceivable,
  buildRecordInterest,
  buildRestoreReceipt,
  buildRestoreReceivable,
  buildSetUseForSpending,
  buildUndoReceipt,
  isInterestEntry,
  receivableStatus,
  type ReceivableInput,
  type AccountInput,
  type ExpectedInput,
  type ExpectedRemoved,
  type TransferInput,
  buildAddFund,
  buildAddIncomeSource,
  buildAddOtherIncome,
  buildBudgetSettingsPatch,
  buildConfirmPayday,
  buildDeleteFund,
  buildDeleteIncomeSource,
  buildEditFund,
  buildEditOtherIncome,
  buildEditIncomeSource,
  buildEditReceivedPayday,
  buildRestoreFund,
  buildSeedBudgetSettings,
  buildSkipPayday,
  buildUndoPayday,
  findPayday,
  localDate,
  newId,
  singletonId,
  type BudgetSettings,
  type BuildCtx,
  type DomainEvent,
  type EffectivePayday,
  type FundInput,
  type IncomeSourceInput,
  type IncomeSourceLike,
  type LocalDate,
  type Mutation,
  type OtherIncomeDraft,
  type OtherIncomeFields,
} from '@tahi/core';
import {
  BUDGET_DASHBOARD_ID,
  buildBudgetDashboardTiles,
} from '../../modules/budget/dashboardDefaults';
import { makeMutation, monotonicNow } from '../mutation';
import * as repo from '../repos/budget';
import { getUserSettings, listTileTypes } from '../repos/spending';
import { runCommand, type CommandEnv } from './dispatch';

export type BudgetCommandResult<T = object> = ({ ok: true } & T) | { ok: false; error: string };

/** Bump only if the defaults change and existing users should get the additions. */
export const BUDGET_SEED = 'budget.v1';

function ctx(env: CommandEnv): BuildCtx {
  return { now: monotonicNow(), tz: env.tz, newId };
}

async function commit(env: CommandEnv, mutations: Mutation[], event?: DomainEvent): Promise<void> {
  if (mutations.length === 0) return;
  await runCommand(env, { mutations, ...(event ? { event } : {}) });
}

async function settingsPatch(
  env: CommandEnv,
  patch: Partial<BudgetSettings>,
  c: BuildCtx,
): Promise<Mutation[]> {
  const settings = await repo.getBudgetSettingsView(env.db, env.userId);
  return buildBudgetSettingsPatch(env.userId, settings.exists, patch, c);
}

// ---------- Funds ----------

/** The first fund (or any fund while there is no default) becomes the default fund. */
export async function addFund(
  env: CommandEnv,
  input: FundInput,
  opts: { makeDefault?: boolean } = {},
): Promise<BudgetCommandResult<{ id: string }>> {
  const c = ctx(env);
  const r = buildAddFund(
    input,
    await repo.listFunds(env.db, env.userId),
    c,
    localDate(c.now, env.tz),
  );
  if (!r.ok) return r;
  const settings = await repo.getBudgetSettingsView(env.db, env.userId);
  const makeDefault = opts.makeDefault ?? settings.defaultFundId === null;
  const extra = makeDefault
    ? buildBudgetSettingsPatch(env.userId, settings.exists, { defaultFundId: r.fundId }, c)
    : [];
  await commit(env, [r.mutation, ...extra]);
  return { ok: true, id: r.fundId };
}

export async function editFund(
  env: CommandEnv,
  id: string,
  input: FundInput,
): Promise<BudgetCommandResult> {
  const r = buildEditFund(id, input, await repo.listFunds(env.db, env.userId), ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true };
}

/** Soft delete; clears the default if it was this fund. Past expenses keep their fundId. */
export async function deleteFund(
  env: CommandEnv,
  id: string,
): Promise<BudgetCommandResult<{ wasDefault: boolean }>> {
  const c = ctx(env);
  const settings = await repo.getBudgetSettingsView(env.db, env.userId);
  const wasDefault = settings.defaultFundId === id;
  const extra = wasDefault
    ? buildBudgetSettingsPatch(env.userId, settings.exists, { defaultFundId: null }, c)
    : [];
  await commit(env, [buildDeleteFund(id, c), ...extra]);
  return { ok: true, wasDefault };
}

/** Undo of deleteFund. */
export async function restoreFund(
  env: CommandEnv,
  id: string,
  opts: { makeDefault?: boolean } = {},
): Promise<BudgetCommandResult> {
  const c = ctx(env);
  const extra = opts.makeDefault ? await settingsPatch(env, { defaultFundId: id }, c) : [];
  await commit(env, [buildRestoreFund(id, c), ...extra]);
  return { ok: true };
}

export async function setDefaultFund(
  env: CommandEnv,
  fundId: string | null,
): Promise<BudgetCommandResult> {
  await commit(env, await settingsPatch(env, { defaultFundId: fundId }, ctx(env)));
  return { ok: true };
}

/** Spec §6: affects new entries only (the rule reads it when an expense is created). */
export async function setAutoDeduct(env: CommandEnv, on: boolean): Promise<BudgetCommandResult> {
  await commit(env, await settingsPatch(env, { autoDeduct: on }, ctx(env)));
  return { ok: true };
}

// ---------- Income sources ----------

export async function addIncomeSource(
  env: CommandEnv,
  input: IncomeSourceInput,
): Promise<BudgetCommandResult<{ id: string }>> {
  const r = buildAddIncomeSource(input, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true, id: r.sourceId };
}

export async function editIncomeSource(
  env: CommandEnv,
  id: string,
  input: IncomeSourceInput,
): Promise<BudgetCommandResult> {
  const r = buildEditIncomeSource(id, input, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true };
}

export async function deleteIncomeSource(
  env: CommandEnv,
  id: string,
): Promise<BudgetCommandResult> {
  await commit(env, [buildDeleteIncomeSource(id, ctx(env))]);
  return { ok: true };
}

// ---------- Paydays ----------

async function loadPayday(
  env: CommandEnv,
  sourceId: string,
  nominalDate: LocalDate,
): Promise<{ payday: EffectivePayday; source: IncomeSourceLike } | null> {
  const sources = await repo.listIncomeSources(env.db, env.userId, { includeDeleted: true });
  const rows = await repo.listPaydayRows(env.db, env.userId);
  const source = sources.find((s) => s.id === sourceId);
  const payday = findPayday(env.userId, sources, rows, sourceId, nominalDate);
  return source && payday ? { payday, source } : null;
}

/** "Payday. Did ₱18,000 come in?" -> confirm, or a different amount and day. */
export async function confirmPayday(
  env: CommandEnv,
  sourceId: string,
  nominalDate: LocalDate,
  got: { amountMinor: number; date: LocalDate; accountId?: string | null },
): Promise<BudgetCommandResult<{ paydayId: string }>> {
  const found = await loadPayday(env, sourceId, nominalDate);
  if (!found) return { ok: false, error: 'not_found' };
  // v0.5: picked on the confirm sheet, else the source's account, else "Receive into".
  const settings = await repo.getBudgetSettingsView(env.db, env.userId);
  const accounts = await repo.listAccounts(env.db, env.userId);
  const accountId = receiveIntoAccount(
    got.accountId,
    found.source,
    settings,
    env.usable('budget'),
    accounts,
  );
  const r = buildConfirmPayday(
    env.userId,
    found.payday,
    found.source,
    { ...got, accountId },
    ctx(env),
  );
  if (!r.ok) return r;
  await commit(env, r.mutations, r.event);
  return { ok: true, paydayId: found.payday.id };
}

/** Bonuses only (spec): skipped for this date; the default stays the same. */
export async function skipPayday(
  env: CommandEnv,
  sourceId: string,
  nominalDate: LocalDate,
): Promise<BudgetCommandResult> {
  const found = await loadPayday(env, sourceId, nominalDate);
  if (!found) return { ok: false, error: 'not_found' };
  const r = buildSkipPayday(found.payday, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation], r.event);
  return { ok: true };
}

/** Undo a confirm or a skip: the payday is expected again. */
export async function undoPayday(
  env: CommandEnv,
  sourceId: string,
  nominalDate: LocalDate,
): Promise<BudgetCommandResult> {
  const found = await loadPayday(env, sourceId, nominalDate);
  if (!found) return { ok: false, error: 'not_found' };
  await commit(env, buildUndoPayday(found.payday, ctx(env)));
  return { ok: true };
}

export async function editReceivedPayday(
  env: CommandEnv,
  sourceId: string,
  nominalDate: LocalDate,
  patch: { amountMinor?: number; date?: LocalDate; accountId?: string | null },
): Promise<BudgetCommandResult> {
  const found = await loadPayday(env, sourceId, nominalDate);
  if (!found) return { ok: false, error: 'not_found' };
  const r = buildEditReceivedPayday(found.payday, patch, ctx(env));
  if (!r.ok) return r;
  await commit(env, r.mutations);
  return { ok: true };
}

// ---------- Other income ----------

export async function addOtherIncome(
  env: CommandEnv,
  draft: OtherIncomeDraft,
): Promise<BudgetCommandResult<{ id: string }>> {
  // v0.5: picked, else Budget's "Receive into" account (only while Budget is usable).
  const settings = await repo.getBudgetSettingsView(env.db, env.userId);
  const accounts = await repo.listAccounts(env.db, env.userId);
  const accountId = receiveIntoAccount(
    draft.accountId,
    null,
    settings,
    env.usable('budget'),
    accounts,
  );
  const r = buildAddOtherIncome({ ...draft, accountId }, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true, id: r.entryId };
}

/** Edit one-off income any time (not a payday's income: that goes through the payday screen). */
export async function editOtherIncome(
  env: CommandEnv,
  id: string,
  patch: Partial<OtherIncomeFields>,
): Promise<BudgetCommandResult<{ undo: Partial<OtherIncomeFields> }>> {
  const e = await repo.getOtherIncome(env.db, env.userId, id);
  if (!e || e.deletedAt) return { ok: false, error: 'not_found' };
  const r = buildEditOtherIncome(id, e, patch, ctx(env));
  if (!r.ok) return r;
  if (r.mutation) await commit(env, [r.mutation]);
  return { ok: true, undo: r.undo };
}

/** Delete one-off income (soft, kept in History); also the undo of addOtherIncome. */
export async function deleteOtherIncome(env: CommandEnv, id: string): Promise<BudgetCommandResult> {
  const e = await repo.getOtherIncome(env.db, env.userId, id);
  if (!e) return { ok: false, error: 'not_found' };
  if (e.deletedAt) return { ok: true };
  await commit(env, [makeMutation('ledger_entries', 'delete', id, undefined, 'budget')]);
  return { ok: true };
}

/** Undo of deleteOtherIncome. */
export async function restoreOtherIncome(
  env: CommandEnv,
  id: string,
): Promise<BudgetCommandResult> {
  const e = await repo.getOtherIncome(env.db, env.userId, id);
  if (!e) return { ok: false, error: 'not_found' };
  if (!e.deletedAt) return { ok: true };
  // A restore is an insert with no values: the applier clears deletedAt (like restoreExpense).
  await commit(env, [makeMutation('ledger_entries', 'insert', id, {}, 'budget')]);
  return { ok: true };
}

/** Undo of deleteIncomeSource. */
export async function restoreIncomeSource(
  env: CommandEnv,
  id: string,
): Promise<BudgetCommandResult> {
  await commit(env, [buildRestoreIncomeSource(id, ctx(env))]);
  return { ok: true };
}

// ---------- Settings (v0.5) ----------

export async function setUseFunds(env: CommandEnv, on: boolean): Promise<BudgetCommandResult> {
  await commit(env, await settingsPatch(env, { useFunds: on }, ctx(env)));
  return { ok: true };
}

/** Budget's "Pay from" account (spending and payments) or "Receive into" account (income). */
export async function setDefaultAccount(
  env: CommandEnv,
  which: 'pay' | 'receive',
  accountId: string | null,
): Promise<BudgetCommandResult> {
  const patch = which === 'pay' ? { defaultAccountId: accountId } : { incomeAccountId: accountId };
  await commit(env, await settingsPatch(env, patch, ctx(env)));
  return { ok: true };
}

// ---------- Accounts (v0.5) ----------

/**
 * The first account becomes "Pay from" and "Receive into" while those are empty (v0.6: "Pay from"
 * only when it is used for spending; a savings-only account is never a spending default).
 */
export async function addAccount(
  env: CommandEnv,
  input: AccountInput,
): Promise<BudgetCommandResult<{ id: string }>> {
  const c = ctx(env);
  const r = buildAddAccount(input, await repo.listAccounts(env.db, env.userId), c);
  if (!r.ok) return r;
  const settings = await repo.getBudgetSettingsView(env.db, env.userId);
  const patch: Partial<BudgetSettings> = {};
  const spends = (r.mutation.values as { useForSpending?: boolean }).useForSpending !== false;
  if (!settings.defaultAccountId && spends) patch.defaultAccountId = r.accountId;
  if (!settings.incomeAccountId) patch.incomeAccountId = r.accountId;
  await commit(env, [
    r.mutation,
    ...buildBudgetSettingsPatch(env.userId, settings.exists, patch, c),
  ]);
  return { ok: true, id: r.accountId };
}

export async function editAccount(
  env: CommandEnv,
  id: string,
  input: AccountInput,
): Promise<BudgetCommandResult> {
  const before = await repo.getAccount(env.db, env.userId, id);
  if (!before || before.deletedAt) return { ok: false, error: 'not_found' };
  const c = ctx(env);
  const r = buildEditAccount(before, input, await repo.listAccounts(env.db, env.userId), c);
  if (!r.ok) return r;
  if (!r.mutation) return { ok: true };
  const off = (r.mutation.values as { useForSpending?: boolean }).useForSpending === false;
  await commit(env, [r.mutation, ...(off ? await clearPayFrom(env, id, c) : [])]);
  return { ok: true };
}

/** v0.6: an account that becomes savings-only stops being Budget's "Pay from" (same command). */
async function clearPayFrom(env: CommandEnv, id: string, c: BuildCtx): Promise<Mutation[]> {
  const settings = await repo.getBudgetSettingsView(env.db, env.userId);
  if (settings.defaultAccountId !== id) return [];
  return buildBudgetSettingsPatch(env.userId, settings.exists, { defaultAccountId: null }, c);
}

/** Soft delete; clears "Pay from" / "Receive into" when they pointed at it. */
export async function deleteAccount(
  env: CommandEnv,
  id: string,
): Promise<BudgetCommandResult<{ wasPay: boolean; wasReceive: boolean }>> {
  const c = ctx(env);
  const settings = await repo.getBudgetSettingsView(env.db, env.userId);
  const wasPay = settings.defaultAccountId === id;
  const wasReceive = settings.incomeAccountId === id;
  const patch: Partial<BudgetSettings> = {};
  if (wasPay) patch.defaultAccountId = null;
  if (wasReceive) patch.incomeAccountId = null;
  await commit(env, [
    buildDeleteAccount(id, c),
    ...buildBudgetSettingsPatch(env.userId, settings.exists, patch, c),
  ]);
  return { ok: true, wasPay, wasReceive };
}

export async function restoreAccount(
  env: CommandEnv,
  id: string,
  opts: { wasPay?: boolean; wasReceive?: boolean } = {},
): Promise<BudgetCommandResult> {
  const c = ctx(env);
  const patch: Partial<BudgetSettings> = {};
  if (opts.wasPay) patch.defaultAccountId = id;
  if (opts.wasReceive) patch.incomeAccountId = id;
  await commit(env, [buildRestoreAccount(id, c), ...(await settingsPatch(env, patch, c))]);
  return { ok: true };
}

/** GoTyme → GCash ₱1,000 (+ optional fee): never spending or income. */
export async function transfer(
  env: CommandEnv,
  input: TransferInput,
): Promise<BudgetCommandResult<{ id: string; feeIds: string[] }>> {
  const r = buildTransfer(input, await repo.listAccounts(env.db, env.userId), ctx(env));
  if (!r.ok) return r;
  await commit(env, r.mutations);
  return { ok: true, id: r.transferId, feeIds: r.feeId ? [r.feeId] : [] };
}

/** Undo or delete a transfer, a balance adjustment or a card payment's fee pair. */
export async function deleteTransfer(env: CommandEnv, id: string): Promise<BudgetCommandResult> {
  const e = await repo.getAccountEntry(env.db, env.userId, id);
  if (!e || e.kind !== 'transfer' || e.sourceModule !== 'budget' || e.sourceId)
    return { ok: false, error: 'not_found' };
  const fees = await repo.listTransferFeeIds(env.db, env.userId, id);
  await commit(env, buildUndoTransfer(id, fees, ctx(env)));
  return { ok: true };
}

export async function restoreTransfer(env: CommandEnv, id: string): Promise<BudgetCommandResult> {
  const fees = await repo.listTransferFeeIds(env.db, env.userId, id);
  await commit(env, buildRestoreTransfer(id, fees, ctx(env)));
  return { ok: true };
}

/** "Adjust balance": type the real balance; the difference is recorded as an adjustment. */
export async function adjustBalance(
  env: CommandEnv,
  accountId: string,
  realMinor: number,
): Promise<BudgetCommandResult<{ id: string | null }>> {
  const c = ctx(env);
  const account = await repo.getAccount(env.db, env.userId, accountId);
  if (!account) return { ok: false, error: 'not_found' };
  const today = localDate(c.now, env.tz);
  const current = accountBalance(account, await repo.listAccountEntries(env.db, env.userId), today);
  const r = buildAdjustBalance(account, current, realMinor, today, c);
  if (!r.ok) return r;
  if (r.mutation) await commit(env, [r.mutation]);
  return { ok: true, id: r.entryId };
}

// ---------- Expected money / Coming in (v0.5) ----------

export async function addExpected(
  env: CommandEnv,
  input: ExpectedInput,
): Promise<BudgetCommandResult<{ id: string }>> {
  const r = buildAddExpected(input, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true, id: r.id };
}

export async function editExpected(
  env: CommandEnv,
  id: string,
  input: ExpectedInput,
): Promise<BudgetCommandResult> {
  const before = await repo.getExpected(env.db, env.userId, id);
  if (!before || before.deletedAt) return { ok: false, error: 'not_found' };
  const r = buildEditExpected(before, input, ctx(env));
  if (!r.ok) return r;
  if (r.mutation) await commit(env, [r.mutation]);
  return { ok: true };
}

/** Soft delete (a repeating item's receipts too); returns what Undo needs to bring back. */
export async function deleteExpected(
  env: CommandEnv,
  id: string,
): Promise<BudgetCommandResult<{ removed: ExpectedRemoved }>> {
  const e = await repo.getExpected(env.db, env.userId, id);
  if (!e || e.deletedAt) return { ok: false, error: 'not_found' };
  const r = buildDeleteExpected(e, ctx(env));
  await commit(env, r.mutations);
  return { ok: true, removed: r.removed };
}

export async function restoreExpected(
  env: CommandEnv,
  id: string,
  removed?: ExpectedRemoved,
): Promise<BudgetCommandResult> {
  const e = await repo.getExpected(env.db, env.userId, id);
  if (!e) return { ok: false, error: 'not_found' };
  await commit(env, buildRestoreExpected(e, ctx(env), removed));
  return { ok: true };
}

/**
 * "Received": the amount can differ and the account can be changed; deposited, never income.
 * v0.7.4: a repeating item names the occurrence (its scheduled day); only that one is received.
 */
export async function receiveExpected(
  env: CommandEnv,
  id: string,
  got: { amountMinor: number; date: LocalDate; accountId: string | null },
  occurrenceDate?: LocalDate,
): Promise<BudgetCommandResult> {
  const e = await repo.getExpected(env.db, env.userId, id);
  if (!e || e.deletedAt) return { ok: false, error: 'not_found' };
  const r = isRepeating(e)
    ? buildReceiveOccurrence(env.userId, e, occurrenceDate ?? '', got, ctx(env))
    : buildReceiveExpected(env.userId, e, got, ctx(env));
  if (!r.ok) return r;
  await commit(env, r.mutations);
  return { ok: true };
}

/** v0.7.4 "Didn't come": skips one occurrence of a repeating item. */
export async function skipExpected(
  env: CommandEnv,
  id: string,
  occurrenceDate: LocalDate,
): Promise<BudgetCommandResult> {
  const e = await repo.getExpected(env.db, env.userId, id);
  if (!e || e.deletedAt) return { ok: false, error: 'not_found' };
  const r = buildSkipOccurrence(env.userId, e, occurrenceDate, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true };
}

/** Undo "Received" (or, for one occurrence of a repeating item, a received or skipped one). */
export async function undoReceiveExpected(
  env: CommandEnv,
  id: string,
  occurrenceDate?: LocalDate,
): Promise<BudgetCommandResult> {
  const e = await repo.getExpected(env.db, env.userId, id);
  if (!e) return { ok: false, error: 'not_found' };
  const r = isRepeating(e)
    ? buildUndoOccurrence(e, occurrenceDate ?? '', ctx(env))
    : buildUndoReceiveExpected(e, ctx(env));
  if (!r.ok) return r;
  await commit(env, r.mutations);
  return { ok: true };
}

// ---------- Savings & investments (v0.6) ----------

/** "Use for spending" on or off (editable any time). */
export async function setUseForSpending(
  env: CommandEnv,
  id: string,
  on: boolean,
): Promise<BudgetCommandResult> {
  const a = await repo.getAccount(env.db, env.userId, id);
  if (!a || a.deletedAt) return { ok: false, error: 'not_found' };
  const c = ctx(env);
  const m = buildSetUseForSpending(a, on, c);
  if (m) await commit(env, [m, ...(on ? [] : await clearPayFrom(env, id, c))]);
  return { ok: true };
}

/** "Interest received": a deposit into the account, never income. */
export async function recordInterest(
  env: CommandEnv,
  accountId: string,
  got: { amountMinor: number; date: LocalDate },
): Promise<BudgetCommandResult<{ id: string }>> {
  const a = await repo.getAccount(env.db, env.userId, accountId);
  if (!a) return { ok: false, error: 'not_found' };
  const r = buildRecordInterest(a, got, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true, id: r.id };
}

/** Undo or delete a recorded interest row (only an interest row). */
export async function deleteInterest(env: CommandEnv, id: string): Promise<BudgetCommandResult> {
  const e = await repo.getAccountEntry(env.db, env.userId, id);
  if (!e || !isInterestEntry(e)) return { ok: false, error: 'not_found' };
  await commit(env, [buildUndoReceipt(id, ctx(env))]);
  return { ok: true };
}

export async function restoreInterest(env: CommandEnv, id: string): Promise<BudgetCommandResult> {
  await commit(env, [buildRestoreReceipt(id, ctx(env))]);
  return { ok: true };
}

// ---------- Owed to you (v0.6) ----------

export async function addReceivable(
  env: CommandEnv,
  input: ReceivableInput,
): Promise<BudgetCommandResult<{ id: string }>> {
  const r = buildAddReceivable(input, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true, id: r.id };
}

export async function editReceivable(
  env: CommandEnv,
  id: string,
  input: ReceivableInput,
): Promise<BudgetCommandResult> {
  const before = await repo.getReceivable(env.db, env.userId, id);
  if (!before || before.deletedAt) return { ok: false, error: 'not_found' };
  const r = buildEditReceivable(before, input, ctx(env));
  if (!r.ok) return r;
  if (r.mutation) await commit(env, [r.mutation]);
  return { ok: true };
}

/** Soft delete with its receipts; returns the receipt ids so Undo brings the same ones back. */
export async function deleteReceivable(
  env: CommandEnv,
  id: string,
): Promise<BudgetCommandResult<{ receiptIds: string[] }>> {
  const r = await repo.getReceivable(env.db, env.userId, id);
  if (!r || r.deletedAt) return { ok: false, error: 'not_found' };
  const receiptIds = (await repo.listBudgetDeposits(env.db, env.userId, { sourceId: id })).map(
    (e) => e.id,
  );
  await commit(env, buildDeleteReceivable(r, receiptIds, ctx(env)));
  return { ok: true, receiptIds };
}

export async function restoreReceivable(
  env: CommandEnv,
  id: string,
  receiptIds: readonly string[],
): Promise<BudgetCommandResult> {
  const r = await repo.getReceivable(env.db, env.userId, id);
  if (!r) return { ok: false, error: 'not_found' };
  await commit(env, buildRestoreReceivable(r, receiptIds, ctx(env)));
  return { ok: true };
}

/** "Received" (part or all) into an account: a deposit, never income or Safe to spend until now. */
export async function receiveReceivable(
  env: CommandEnv,
  id: string,
  got: { amountMinor: number; date: LocalDate; accountId: string | null },
): Promise<BudgetCommandResult<{ receiptId: string }>> {
  const r = await repo.getReceivable(env.db, env.userId, id);
  if (!r || r.deletedAt) return { ok: false, error: 'not_found' };
  const receipts = await repo.listBudgetDeposits(env.db, env.userId, { sourceId: id });
  const built = buildReceiveReceivable(receivableStatus(r, receipts), got, ctx(env));
  if (!built.ok) return built;
  await commit(env, [built.mutation]);
  return { ok: true, receiptId: built.id };
}

export async function undoReceipt(
  env: CommandEnv,
  receiptId: string,
): Promise<BudgetCommandResult> {
  await commit(env, [buildUndoReceipt(receiptId, ctx(env))]);
  return { ok: true };
}

export async function restoreReceipt(
  env: CommandEnv,
  receiptId: string,
): Promise<BudgetCommandResult> {
  await commit(env, [buildRestoreReceipt(receiptId, ctx(env))]);
  return { ok: true };
}

// ---------- Seeding ----------

/**
 * Runs whenever Budget becomes usable (register.tsx, onModuleUsable). Insert-if-absent with seeded
 * ids, so it never overwrites settings or tiles, even ones this device hasn't pulled yet.
 * No default fund is seeded (conventions §19): the first fund the user adds becomes the default.
 */
export async function seedBudgetDefaults(env: CommandEnv): Promise<void> {
  const { db, userId } = env;
  const userSettings = await getUserSettings(db, userId);
  if (userSettings?.seeds[BUDGET_SEED]) return;
  const c = ctx(env);
  const tileTypes = new Set(await listTileTypes(db, userId, BUDGET_DASHBOARD_ID));
  const seeds = { ...userSettings?.seeds, [BUDGET_SEED]: c.now };
  const settingsId = singletonId(userId, 'user_settings');
  const marker: Mutation = userSettings
    ? {
        id: newId(),
        table: 'user_settings',
        op: 'update',
        rowId: settingsId,
        values: { seeds },
        at: c.now,
        source: 'budget',
      }
    : {
        id: newId(),
        table: 'user_settings',
        op: 'insert',
        rowId: settingsId,
        values: { weekStart: 1, seeds },
        at: c.now,
        source: 'budget',
        ifAbsent: true,
      };
  await commit(env, [
    buildSeedBudgetSettings(userId, c),
    ...buildBudgetDashboardTiles(userId, tileTypes, c),
    marker,
  ]);
}
